import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Fixed lateral rail that rotates active FUTURO ADS side banners.
 * Renders nothing when no slot is configured or no approved ads are available.
 * Hidden on screens < xl (≥ 1280px) to preserve mobile / small-desktop UX.
 */
export default function AdBannerRail({ side }) {
  const location = useLocation();
  const [payload, setPayload] = useState(null);
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const ctrl = new AbortController();
    const load = async () => {
      try {
        const r = await axios.get(`${API}/ads2/banners/rotation`, {
          params: { side, page: location.pathname || "*", limit: 6 },
          signal: ctrl.signal,
        });
        setPayload(r.data);
        setIdx(0);
      } catch (err) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        setPayload(null);
      }
    };
    load();
    return () => ctrl.abort();
  }, [side, location.pathname]);

  useEffect(() => {
    if (!payload?.rotation?.length || payload.rotation.length < 2) return undefined;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % payload.rotation.length);
    }, payload.rotate_every_ms || 7000);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [payload]);

  // Track impression whenever the visible ad changes
  useEffect(() => {
    const ad = payload?.rotation?.[idx];
    if (!ad?.ad_id) return;
    axios.post(`${API}/ads2/banners/track`, {
      ad_id: ad.ad_id, event: "impression", page: location.pathname, side,
    }).catch(() => {});
  }, [idx, payload, side, location.pathname]);

  if (!payload || !payload.rotation?.length) return null;

  const ad = payload.rotation[idx];
  const dims = payload.dimensions || { width: 200, height: 400 };

  const onClick = () => {
    axios.post(`${API}/ads2/banners/track`, {
      ad_id: ad.ad_id, event: "click", page: location.pathname, side,
    }).catch(() => {});
  };

  const positionCls = side === "left" ? "left-3" : "right-3";

  return (
    <aside
      className={`hidden 2xl:block fixed top-24 ${positionCls} z-30 pointer-events-none`}
      data-testid={`ad-rail-${side}`}
    >
      <div className="pointer-events-auto" style={{ width: dims.width }}>
        <div className="mb-1 flex items-center gap-1">
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-900/80 text-white">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Patrocinado
          </span>
        </div>
        <a
          href={ad.destination_url || "#"}
          target="_blank"
          rel="sponsored noreferrer noopener"
          onClick={onClick}
          data-testid={`ad-rail-${side}-link`}
          className="block group rounded-xl overflow-hidden border border-border shadow-sm hover:shadow-lg transition-shadow bg-card"
          style={{ height: dims.height }}
        >
          <div
            className="w-full bg-slate-100 overflow-hidden"
            style={{ height: Math.floor(dims.height * 0.6) }}
          >
            <img
              src={ad.media_url}
              alt={ad.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
          <div className="p-2.5">
            <p className="text-xs font-bold line-clamp-2">{ad.title}</p>
            <p className="text-[10px] text-muted-foreground mt-1 line-clamp-3">{ad.primary_text}</p>
          </div>
        </a>
        {payload.rotation.length > 1 && (
          <div className="flex justify-center gap-1 mt-1.5" data-testid={`ad-rail-${side}-dots`}>
            {payload.rotation.map((_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full transition-all ${i === idx ? "w-4 bg-primary" : "w-1.5 bg-slate-300"}`}
              />
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}
